import { useEffect, useState, type CSSProperties } from 'react';
import { IState } from '../../redux/redux';
import type { ISettings } from '../../redux/types';
import Navigation from '../Navigation/navigation';
import { EasyImage } from '../Tools/EasyImage';
import StoreX from '../../redux/oldStore';

export type HeaderLayout = 'stacked' | 'bar' | 'banner' | 'split';

/** Missing, 0, and unknown values stay on the header that ships today. */
export function headerLayoutOf(value?: string | null): HeaderLayout {
  switch ((value ?? '').toLowerCase()) {
    case 'bar':
    case '1':
      return 'bar';
    case 'banner':
    case '2':
      return 'banner';
    case 'split':
    case '3':
      return 'split';
    default:
      return 'stacked';
  }
}

export type HeaderJustify = 'layout' | 'left' | 'center' | 'right';
export type HeaderFontSize = '' | 's' | 'm' | 'l' | 'xl';

const HEADER_FONTS: Record<string, string> = {
  inter: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  georgia: "Georgia, 'Times New Roman', serif",
  palatino: "Palatino, 'Palatino Linotype', 'Book Antiqua', serif",
  garamond: "Garamond, 'Palatino Linotype', serif",
  times: "'Times New Roman', Times, serif",
  arial: 'Arial, Helvetica, sans-serif',
  verdana: 'Verdana, Geneva, sans-serif',
  trebuchet: "'Trebuchet MS', 'Segoe UI', sans-serif",
  courier: "'Courier New', Courier, monospace",
};

/** Missing and unknown values keep each header style as it ships. */
export function headerJustifyOf(value?: string | null): HeaderJustify {
  switch ((value ?? '').toLowerCase()) {
    case 'left':
    case '1':
      return 'left';
    case 'center':
    case '2':
      return 'center';
    case 'right':
    case '3':
      return 'right';
    default:
      return 'layout';
  }
}

export function headerFontSizeOf(value?: string | null): HeaderFontSize {
  switch ((value ?? '').toLowerCase()) {
    case 's':
    case 'small':
      return 's';
    case 'm':
    case 'medium':
      return 'm';
    case 'l':
    case 'large':
      return 'l';
    case 'xl':
      return 'xl';
    default:
      return '';
  }
}

/** Allowlisted stacks only, so a settings value cannot inject CSS. */
export function headerFontFamily(value?: string | null): string | null {
  const key = (value ?? '').trim().toLowerCase();
  return HEADER_FONTS[key] ?? null;
}

export function headerColorOf(value?: string | null): string | null {
  const trimmed = (value ?? '').trim();
  if (/^#[0-9a-fA-F]{3}$/.test(trimmed) || /^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed;
  return null;
}

/** Missing means shown, so an older settings payload does not hide the header. */
export function headerShown(value?: boolean | null): boolean {
  return value !== false;
}

export function headerShellClass(settings: ISettings | null | undefined, hideLayout: boolean): string {
  const classes = ['x-style', `x-header-${headerLayoutOf(settings?.headerLayout)}`];
  const justify = headerJustifyOf(settings?.headerJustify);
  if (justify !== 'layout') classes.push(`x-header-justify-${justify}`);
  const size = headerFontSizeOf(settings?.headerFontSize);
  if (size) classes.push(`x-header-size-${size}`);
  if (headerFontFamily(settings?.headerFont)) classes.push('x-header-custom-font');
  if (headerColorOf(settings?.headerColor)) classes.push('x-header-custom-color');
  if (hideLayout) classes.push('hide-layout-stuff');
  return classes.join(' ');
}

export function headerShellStyle(settings: ISettings | null | undefined): CSSProperties | undefined {
  const color = headerColorOf(settings?.headerColor);
  const font = headerFontFamily(settings?.headerFont);
  if (!color && !font) return undefined;
  const style: Record<string, string> = {};
  if (color) style['--x-header-title-color'] = color;
  if (font) style['--x-header-title-font'] = font;
  return style as CSSProperties;
}

const NHD_LOGO = '../api/FileApi/FileFetcherClient/e99bb006-500d-40fb-82df-6946fe3e383a';
const NHD_MARK = 'https://zfairs.com/file/NHD.svg';

function httpUrl(value?: string | null): string | null {
  const trimmed = (value ?? '').trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return null;
}

function cssImageUrl(value?: string | null): string | null {
  const trimmed = httpUrl(value);
  if (!trimmed || /["'()\\\s]/.test(trimmed)) return null;
  return trimmed;
}

interface IProps {
  state: IState;
  useOld: boolean;
}

const Header = (props: IProps) => {
  const [ready, setReady] = useState<boolean>(false);
  const [headingStyle, setHeadingStyle] = useState<'std' | 'nhd'>('std');

  useEffect(() => {
    if (props.state.SettingsResponse) {
      setReady(true);

      if (props.state.SettingsResponse.Settings.isHistoryContest) {
        setHeadingStyle('nhd');
      } else {
        setHeadingStyle('std');
      }
    }
  }, [props.state]);

  const response = props.state.SettingsResponse;
  const name = response?.Name ?? '';
  const home = StoreX.BuildUrl('../');
  const logoSrc = response?.Logo ?? '';
  const showNhd = !!props.state.Settings?.isHistoryContest;
  const bannerImage = cssImageUrl(props.state.Settings?.headerBannerImage);
  const partnerLogo = cssImageUrl(props.state.Settings?.headerPartnerLogo);
  const partnerLink = httpUrl(props.state.Settings?.headerPartnerUrl);
  const primaryCaption = (props.state.Settings?.headerPrimaryCaption ?? '').trim();
  const partnerCaption = (props.state.Settings?.headerPartnerCaption ?? '').trim();
  const tagline = (props.state.Settings?.headerTagline ?? '').trim();
  const partner = partnerLogo
    ? { src: partnerLogo, href: partnerLink, alt: 'Partner logo' }
    : showNhd
      ? { src: NHD_MARK, href: 'https://nhd.org', alt: 'NHD logo' }
      : null;

  const logoMark = (alt: string, linked: boolean) =>
    logoSrc.length > 0 ? (
      <div className="x-logo">
        {linked ? (
          <a href={home}>
            <EasyImage src={logoSrc} alt={alt} />
          </a>
        ) : (
          <EasyImage src={logoSrc} alt={alt} />
        )}
      </div>
    ) : null;

  const imageMark = (src: string, alt: string, href: string | null) => (
    <div className="x-logo">
      {href ? (
        <a href={href}>
          <EasyImage src={src} alt={alt} />
        </a>
      ) : (
        <EasyImage src={src} alt={alt} />
      )}
    </div>
  );

  const taglineLine = tagline ? <p className="x-header-tagline">{tagline}</p> : null;
  const showLogo = headerShown(props.state.Settings?.headerShowLogo) && logoSrc.length > 0;
  const showTitle = headerShown(props.state.Settings?.headerShowTitle);
  const altTitle = (props.state.Settings?.headerAltTitle ?? '').trim();
  const visibleTitle = showTitle ? name : altTitle;
  const titleNode = (
    <h1 className={`x-header-name${visibleTitle ? '' : ' sr-only'}`}>{visibleTitle || name}</h1>
  );
  const logoClass = showLogo ? ' x-header-has-logo' : '';

  const renderNewHeader = () => {
    const layout = headerLayoutOf(props.state.Settings?.headerLayout);
    if (layout === 'bar') {
      return (
        <div className="x-container">
          <div className="x-header-row">
            <a className={`x-header-brand${logoClass}`} href={home}>
              {showLogo ? logoMark('logo', false) : null}
              {titleNode}
            </a>
            <div className="x-menu">
              <Navigation useOld={false} />
            </div>
          </div>
        </div>
      );
    }
    if (layout === 'banner') {
      return (
        <>
          <div className="x-banner-stage">
            {bannerImage ? (
              <div className="x-banner-photo" style={{ backgroundImage: `url("${bannerImage}")` }} />
            ) : null}
            <div className="x-container">
              <div className={`x-banner-copy${logoClass}`}>
                {showLogo ? logoMark('logo', true) : null}
                <div className="x-banner-text">
                  {titleNode}
                  {taglineLine}
                </div>
              </div>
            </div>
          </div>
          <div className="x-banner-menu">
            <div className="x-container">
              <div className="x-menu">
                <Navigation useOld={false} />
              </div>
            </div>
          </div>
        </>
      );
    }
    if (layout === 'split') {
      return (
        <>
          <div className="x-split-top">
            <div className="x-container x-split-grid">
              <div className="x-split-side">
                {showLogo && primaryCaption ? <span className="x-split-caption">{primaryCaption}</span> : null}
                {showLogo ? logoMark('logo', true) : null}
              </div>
              <div className="x-split-copy">
                {titleNode}
                {taglineLine}
              </div>
              <div className="x-split-side">
                {partner && partnerCaption ? <span className="x-split-caption">{partnerCaption}</span> : null}
                {partner ? imageMark(partner.src, partner.alt, partner.href) : null}
              </div>
            </div>
          </div>
          <div className="x-split-menu">
            <div className="x-container">
              <div className="x-menu">
                <Navigation useOld={false} />
              </div>
            </div>
          </div>
        </>
      );
    }
    return (
      <div className="x-container">
        <div className="">
          <div className="x-logo-">
            {/* <a href={StoreX.BuildUrl('../')}>
              <EasyImage src={props.state.SettingsResponse.Logo} alt="logo" />
            </a> */}
          </div>
          <div className="x-menu">
            <Navigation useOld={props.useOld} />
          </div>
        </div>
        <div className={`x-title${logoClass}`}>
          {showLogo ? logoMark('logo', true) : null}
          {titleNode}
        </div>
      </div>
    );
  };

  return (
    <>
      {props.state.SettingsResponse && (
        <>
          {props.useOld ? (
            <>
              {ready && headingStyle === 'std' && (
                <>
                  <div className="title-bar primary-bg no-print">
                    {props.state.SettingsResponse && props.state.SettingsResponse.Logo?.length > 0 && (
                      <div className="logo">
                        <EasyImage src={props.state.SettingsResponse.Logo} alt="logo" link={StoreX.BuildUrl(``)} />
                      </div>
                    )}
                    <div>{props.state.SettingsResponse && <h1>{props.state.SettingsResponse.Name}</h1>}</div>
                    <div className="logo"></div>
                  </div>
                  <Navigation useOld={props.useOld} />
                </>
              )}

              {ready && headingStyle === 'nhd' && (
                <>
                  <div className="title-bar primary-bg no-print">
                    <div className="logo nhd-logo">
                      <EasyImage src={StoreX.BuildUrl(NHD_LOGO)} alt="nhd logo" />
                    </div>
                    <div>{props.state.SettingsResponse && <h1>{props.state.SettingsResponse.Name}</h1>}</div>
                    {props.state.SettingsResponse && props.state.SettingsResponse.Logo?.length > 0 && (
                      <div className="logo">
                        <EasyImage src={props.state.SettingsResponse.Logo} alt="logo" />
                      </div>
                    )}
                  </div>
                  <Navigation  useOld={props.useOld} />
                </>
              )}
            </>
          ) : (
            renderNewHeader()
          )}
        </>
      )}
    </>
  );
};

export default Header;
